import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { api, fmtMoney, formatErr } from "@/lib/api";
import Modal, { inputCls, inputErrCls, FieldError, labelCls, btnPrimary, btnAccent, btnOutline } from "@/components/Modal";
import NumberInput from "@/components/NumberInput";
import { useFormErrors } from "@/lib/formErrors";
import { TableCard, StatusBadge, EmptyRow, theadCls, thCls, thNumCls, tdCls, tdNumCls, rowCls } from "@/components/Table";
import Pagination from "@/components/Pagination";
import { Plus, FileText, Wallet, Download, Undo2, ChevronRight, ShoppingCart, Landmark, CalendarDays, Truck, Eye, Pencil, Trash2 } from "lucide-react";
import { downloadPurchaseGroupInvoice, downloadPurchaseInvoice } from "@/lib/pdf";
import PurchaseOrders from "@/pages/PurchaseOrders";
import { toast } from "sonner";

const empty = { supplier_id: "", product_id: "", quantity: 1, reorder_level: "", expiry_date: "", date: "", invoice_no: "", paid_amount: 0, notes: "" };
const PAGE = 20;
const todayInput = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Karachi" });
const thisYm = () => todayInput().slice(0, 7);
// Keys touched by a purchase/return/payment — refresh every affected screen at once.
const PURCHASE_KEYS = ["purchases", "purchase-invoices", "products", "suppliers", "supplier",
  "payables", "dashboard", "financial-position", "pnl-series", "movements", "notifications",
  "report-stock", "report-cash", "report-pnl"];

const TABS = [
  { key: "orders", label: "Purchase Orders" },
  { key: "invoices", label: "Invoices" },
  { key: "items", label: "All Items" },
  { key: "returns", label: "Returns" },
];

function Stat({ label, value, icon: Icon, tone = "forest", hint }) {
  const toneCls = tone === "danger" ? "text-agri-danger bg-red-50" : tone === "amber" ? "text-agri-amber-dark bg-agri-amber/10" : "text-agri-forest bg-agri-forest/10";
  return (
    <div className="card-soft p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="label-cap">{label}</div>
          <div className="mt-2 text-2xl font-bold tabular-nums text-agri-ink truncate">{value}</div>
          {hint && <div className="text-[11px] text-agri-ink-2 mt-1">{hint}</div>}
        </div>
        <div className={`h-10 w-10 shrink-0 rounded-lg grid place-items-center ${toneCls}`}><Icon size={18} /></div>
      </div>
    </div>
  );
}

export default function Purchases() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("invoices");
  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [tab]);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [payMethod, setPayMethod] = useState("paid");
  const [payEdit, setPayEdit] = useState(null);
  const [em, setEm] = useState("paid");
  const [ePaid, setEPaid] = useState(0);
  const [invoiceDetail, setInvoiceDetail] = useState(null);   // grouped invoice being viewed
  const [editInv, setEditInv] = useState(null);               // grouped invoice being edited
  const [editLines, setEditLines] = useState([]);             // [{id, name, cost, qty, removed}]
  const [editDate, setEditDate] = useState("");
  const [editInvNo, setEditInvNo] = useState("");

  const { data: suppliers = [] } = useQuery({ queryKey: ["suppliers"], queryFn: () => api.get("/suppliers").then((r) => r.data) });
  const { data: products = [] } = useQuery({ queryKey: ["products"], queryFn: () => api.get("/products").then((r) => r.data) });
  const { data: invoices = [] } = useQuery({ queryKey: ["purchase-invoices"], queryFn: () => api.get("/purchases/invoices").then((r) => r.data) });

  // Items / Returns tabs — server-paginated by kind.
  const listKind = tab === "returns" ? "returns" : "purchases";
  const { data: listData } = useQuery({
    queryKey: ["purchases", listKind, page],
    queryFn: () => api.get("/purchases", { params: { kind: listKind, skip: page * PAGE, limit: PAGE } })
      .then((r) => ({ items: r.data, total: Number(r.headers["x-total-count"] ?? r.data.length) })),
    placeholderData: keepPreviousData,
    enabled: tab === "items" || tab === "returns",
  });
  const rows = listData?.items ?? [];
  const rowsTotal = listData?.total ?? 0;

  // Invoice drill-down — line items for one supplier + day.
  const { data: invItems = [] } = useQuery({
    queryKey: ["purchase-invoice-items", invoiceDetail?.supplier_id, invoiceDetail?.date],
    queryFn: () => api.get("/purchases", { params: { supplier_id: invoiceDetail.supplier_id, date: invoiceDetail.date, kind: "purchases", limit: 500 } }).then((r) => r.data),
    enabled: !!invoiceDetail,
  });

  const fetchInvLines = (g) => api.get("/purchases", { params: { supplier_id: g.supplier_id, date: g.date, kind: "purchases", limit: 500 } }).then((r) => r.data);
  const openInvEdit = async (g) => {
    const items = await fetchInvLines(g);
    setEditLines(items.map((x) => ({ id: x.id, name: x.product_name, cost: x.purchase_price || 0, qty: x.quantity, removed: false })));
    setEditDate(g.date || "");
    setEditInvNo(items[0]?.invoice_no || "");
    setEditInv(g);
  };
  const saveInv = useMutation({
    mutationFn: (force = false) => {
      const remove_ids = editLines.filter((l) => l.removed || (parseFloat(l.qty) || 0) <= 0).map((l) => l.id);
      const edits = editLines.filter((l) => !l.removed && (parseFloat(l.qty) || 0) > 0).map((l) => ({ id: l.id, quantity: parseFloat(l.qty) }));
      if (!edits.length && !remove_ids.length) return Promise.reject({ response: { data: { detail: "Nothing to save" } } });
      return api.put("/purchases/invoice", { edits, remove_ids, date: editDate || undefined, invoice_no: editInvNo, force });
    },
    onSuccess: () => { toast.success("Invoice updated"); PURCHASE_KEYS.forEach((k) => qc.invalidateQueries({ queryKey: [k] })); setEditInv(null); },
    onError: (e) => {
      if (e.response?.status === 409 && window.confirm(formatErr(e.response.data.detail) + "\n\nProceed anyway?")) saveInv.mutate(true);
      else toast.error(formatErr(e.response?.data?.detail));
    },
  });
  const delInv = useMutation({
    mutationFn: async ({ g, force = false }) => {
      const items = await fetchInvLines(g);
      return api.put("/purchases/invoice", { edits: [], remove_ids: items.map((x) => x.id), force });
    },
    onSuccess: () => { toast.success("Invoice deleted"); PURCHASE_KEYS.forEach((k) => qc.invalidateQueries({ queryKey: [k] })); },
    onError: (e, vars) => {
      if (e.response?.status === 409 && window.confirm(formatErr(e.response.data.detail) + "\n\nProceed anyway?")) delInv.mutate({ g: vars.g, force: true });
      else toast.error(formatErr(e.response?.data?.detail));
    },
  });
  const setEditQty = (id, v) => setEditLines((ls) => ls.map((l) => l.id === id ? { ...l, qty: v } : l));
  const toggleRemove = (id) => setEditLines((ls) => ls.map((l) => l.id === id ? { ...l, removed: !l.removed } : l));
  const editTotal = editLines.filter((l) => !l.removed).reduce((s, l) => s + (parseFloat(l.qty) || 0) * l.cost, 0);

  // KPIs
  const totalPurchased = invoices.reduce((s, g) => s + (g.total || 0), 0);
  const payable = suppliers.reduce((s, x) => s + Math.max(x.pending_payment || 0, 0), 0);
  const monthTotal = invoices.filter((g) => (g.date || "").slice(0, 7) === thisYm()).reduce((s, g) => s + (g.total || 0), 0);

  // Cost comes from the selected product (the product owns its price).
  const selProd = products.find((p) => p.id === form.product_id);
  const unitCost = selProd?.purchase_price || 0;
  const billTotal = (parseFloat(form.quantity) || 0) * unitCost;
  const paidNow = payMethod === "paid" ? billTotal : payMethod === "credit" ? 0 : Math.min(parseFloat(form.paid_amount) || 0, billTotal);
  const owed = Math.max(0, billTotal - paidNow);

  const fe = useFormErrors();
  const save = useMutation({
    mutationFn: () => api.post("/purchases", {
      supplier_id: form.supplier_id, product_id: form.product_id,
      quantity: parseFloat(form.quantity),
      paid_amount: paidNow, invoice_no: form.invoice_no, notes: form.notes,
      reorder_level: form.reorder_level === "" ? undefined : parseFloat(form.reorder_level),
      expiry_date: form.expiry_date || undefined,
      date: form.date || undefined,
    }),
    onSuccess: () => { toast.success("Purchase recorded"); PURCHASE_KEYS.forEach((k) => qc.invalidateQueries({ queryKey: [k] })); setOpen(false); setForm(empty); setPayMethod("paid"); fe.clear(); },
    onError: (e) => fe.handleError(e, toast.error),
  });

  const eTotal = payEdit?.total || 0;
  const eNewPaid = em === "paid" ? eTotal : em === "credit" ? 0 : Math.min(parseFloat(ePaid) || 0, eTotal);
  const eOwed = Math.max(0, eTotal - eNewPaid);
  const openPayEdit = (p) => {
    setPayEdit(p);
    setEm(p.payment_status === "partial" ? "partial" : p.payment_status === "credit" ? "credit" : "paid");
    setEPaid(p.paid_amount || 0);
  };
  const savePay = useMutation({
    mutationFn: () => api.patch(`/purchases/${payEdit.id}/payment`, { paid_amount: eNewPaid }),
    onSuccess: () => { toast.success("Payment updated"); PURCHASE_KEYS.forEach((k) => qc.invalidateQueries({ queryKey: [k] })); setPayEdit(null); },
    onError: (e) => toast.error(formatErr(e.response?.data?.detail)),
  });

  const [retPur, setRetPur] = useState(null);
  const [retQty, setRetQty] = useState(1);
  const [retSettle, setRetSettle] = useState("credit");
  const [retNotes, setRetNotes] = useState("");
  const openReturn = (p) => { setRetPur(p); setRetQty(p.quantity); setRetSettle(p.credit_amount > 0 ? "credit" : "cash"); setRetNotes(""); };
  const retTotal = retPur ? (Math.min(parseFloat(retQty) || 0, retPur.quantity)) * (retPur.purchase_price || 0) : 0;
  const doReturn = useMutation({
    mutationFn: () => api.post(`/purchases/${retPur.id}/return`, { quantity: parseFloat(retQty), settlement: retSettle, notes: retNotes }),
    onSuccess: () => { toast.success("Purchase return recorded"); PURCHASE_KEYS.forEach((k) => qc.invalidateQueries({ queryKey: [k] })); setRetPur(null); },
    onError: (e) => toast.error(formatErr(e.response?.data?.detail)),
  });

  const dt = (v) => new Date(v).toLocaleDateString(undefined, { timeZone: "Asia/Karachi" });

  return (
    <div className="space-y-6" data-testid="purchases-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div><div className="label-cap">Procurement</div><h1 className="font-heading text-3xl mt-1">Purchase Orders</h1></div>
        {tab !== "orders" && (
          <button onClick={() => { fe.clear(); setForm({ ...empty, date: todayInput() }); setPayMethod("paid"); setOpen(true); }} className={btnAccent + " flex items-center gap-2"}><Plus size={16} /> Record Purchase</button>
        )}
      </div>

      {/* Summary */}
      {tab !== "orders" && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label="Total Purchased" value={fmtMoney(totalPurchased)} icon={ShoppingCart} hint="All purchase invoices" />
          <Stat label="Outstanding Payable" value={fmtMoney(payable)} icon={Landmark} tone={payable > 0 ? "danger" : "forest"} hint="Owed to suppliers" />
          <Stat label="This Month" value={fmtMoney(monthTotal)} icon={CalendarDays} tone="amber" hint="Purchased this month" />
          <Stat label="Suppliers" value={suppliers.length} icon={Truck} hint={`${invoices.length} invoices`} />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[rgba(28,28,30,0.1)]">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${tab === t.key ? "border-agri-forest text-agri-forest" : "border-transparent text-agri-ink-2 hover:text-agri-ink"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Purchase Orders (pre-purchase) tab */}
      {tab === "orders" && <PurchaseOrders suppliers={suppliers} products={products} />}

      {/* Invoices tab */}
      {tab === "invoices" && (
        <div className="card-soft">
          <div className="p-5 border-b border-[rgba(28,28,30,0.06)]">
            <h3 className="font-heading text-xl">Purchase Invoices</h3>
            <p className="text-xs text-agri-ink-2 mt-0.5">One invoice per supplier per day. Tap a row to see its products; download for the printable invoice.</p>
          </div>
          <TableCard className="!shadow-none">
            <thead className={theadCls}>
              <tr>
                <th className={thCls}>Date</th><th className={thCls}>Supplier</th>
                <th className={thNumCls}>Items</th><th className={thNumCls}>Total</th><th className={thNumCls}>Payable</th><th className={thCls}></th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 && <EmptyRow colSpan={6} icon={FileText}>No purchase invoices yet.</EmptyRow>}
              {invoices.map((g) => (
                <tr key={g.key} className={`${rowCls} cursor-pointer hover:bg-agri-bg/60`} onClick={() => setInvoiceDetail(g)}>
                  <td data-label="Date" className={`${tdCls} whitespace-nowrap`}>{dt(g.date)}</td>
                  <td data-label="Supplier" className={`${tdCls} font-medium`}>{g.supplier_name}</td>
                  <td data-label="Items" className={tdNumCls}>{g.items}</td>
                  <td data-label="Total" className={`${tdNumCls} font-semibold`}>{fmtMoney(g.total)}</td>
                  <td data-label="Payable" className={`${tdNumCls} ${g.credit > 0 ? "text-agri-danger" : "text-agri-ink-2"}`}>{fmtMoney(g.credit)}</td>
                  <td className={`${tdCls} text-right rt-actions whitespace-nowrap`}>
                    <button onClick={(e) => { e.stopPropagation(); setInvoiceDetail(g); }} title="View invoice"
                      className="p-1.5 rounded hover:bg-agri-forest/5 text-agri-ink-2 hover:text-agri-forest align-middle"><Eye size={15} /></button>
                    <button onClick={(e) => { e.stopPropagation(); openInvEdit(g); }} title="Edit invoice"
                      className="p-1.5 rounded hover:bg-agri-forest/5 text-agri-ink-2 hover:text-agri-forest align-middle ml-0.5"><Pencil size={15} /></button>
                    <button onClick={(e) => { e.stopPropagation(); downloadPurchaseGroupInvoice(g); }} title="Download PDF"
                      className="p-1.5 rounded hover:bg-agri-amber/10 text-agri-ink-2 hover:text-agri-amber-dark align-middle ml-0.5"><Download size={15} /></button>
                    <button onClick={(e) => { e.stopPropagation(); if (window.confirm(`Delete the ${g.supplier_name} invoice of ${dt(g.date)} (${g.items} item${g.items === 1 ? "" : "s"}, ${fmtMoney(g.total)})? Stock, payable and cash will be reversed.`)) delInv.mutate({ g }); }} title="Delete invoice"
                      className="p-1.5 rounded hover:bg-red-50 text-agri-ink-2 hover:text-agri-danger align-middle ml-0.5"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableCard>
        </div>
      )}

      {/* All Items tab */}
      {tab === "items" && (
        <>
          <TableCard>
            <thead className={theadCls}>
              <tr>
                <th className={thCls}>Date</th><th className={thCls}>Supplier</th><th className={thCls}>Product</th>
                <th className={thNumCls}>Qty</th><th className={thNumCls}>Cost</th><th className={thNumCls}>Total</th>
                <th className={thCls}>Status</th><th className={thNumCls}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <EmptyRow colSpan={8} icon={FileText}>No purchases yet.</EmptyRow>}
              {rows.map((p) => (
                <tr key={p.id} className={rowCls}>
                  <td data-label="Date" className={`${tdCls} text-agri-ink-2 whitespace-nowrap`}>{dt(p.date || p.created_at)}</td>
                  <td data-label="Supplier" className={`${tdCls} font-medium`}>{p.supplier_name}</td>
                  <td data-label="Product" className={tdCls}>{p.product_name}</td>
                  <td data-label="Qty" className={tdNumCls}>{p.quantity}</td>
                  <td data-label="Cost" className={tdNumCls}>{fmtMoney(p.purchase_price)}</td>
                  <td data-label="Total" className={`${tdNumCls} font-semibold`}>{fmtMoney(p.total)}</td>
                  <td data-label="Status" className={tdCls}>
                    <StatusBadge status={p.payment_status} />
                    {p.credit_amount > 0 && <span className="ml-1 text-xs text-agri-danger">({fmtMoney(p.credit_amount)})</span>}
                  </td>
                  <td data-label="Actions" className={`${tdNumCls} rt-actions whitespace-nowrap`}>
                    <button data-testid={`edit-payment-${p.id}`} onClick={() => openPayEdit(p)} title="Edit payment"
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-[rgba(28,28,30,0.12)] hover:bg-agri-forest/5 text-agri-forest">
                      <Wallet size={13} /> Payment
                    </button>
                    <button data-testid={`return-purchase-${p.id}`} onClick={() => openReturn(p)} title="Return to supplier"
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-[rgba(28,28,30,0.12)] hover:bg-red-50 text-agri-ink-2 hover:text-agri-danger ml-1">
                      <Undo2 size={13} /> Return
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableCard>
          <Pagination page={page} pageSize={PAGE} total={rowsTotal} onPage={setPage} />
        </>
      )}

      {/* Returns tab */}
      {tab === "returns" && (
        <>
          <TableCard>
            <thead className={theadCls}>
              <tr>
                <th className={thCls}>Date</th><th className={thCls}>Supplier</th><th className={thCls}>Product</th>
                <th className={thNumCls}>Qty</th><th className={thNumCls}>Value</th><th className={thCls}>Settlement</th><th className={thNumCls}>Invoice</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <EmptyRow colSpan={7} icon={Undo2}>No purchase returns yet.</EmptyRow>}
              {rows.map((p) => (
                <tr key={p.id} className={rowCls}>
                  <td data-label="Date" className={`${tdCls} text-agri-ink-2 whitespace-nowrap`}>{dt(p.date || p.created_at)}</td>
                  <td data-label="Supplier" className={`${tdCls} font-medium`}>{p.supplier_name}</td>
                  <td data-label="Product" className={tdCls}>{p.product_name}</td>
                  <td data-label="Qty" className={`${tdNumCls} text-agri-danger`}>{p.quantity}</td>
                  <td data-label="Value" className={`${tdNumCls} font-semibold text-agri-danger`}>{fmtMoney(p.total)}</td>
                  <td data-label="Settlement" className={tdCls}>
                    <span className="text-xs">{(p.paid_amount || 0) < 0 ? "Cash refund" : "Reduced payable"}</span>
                  </td>
                  <td data-label="Invoice" className={`${tdNumCls} rt-actions`}>
                    <button onClick={() => downloadPurchaseInvoice(p)} title="Download return invoice"
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-[rgba(28,28,30,0.12)] hover:bg-agri-amber/10 text-agri-ink-2 hover:text-agri-amber-dark">
                      <Download size={13} /> Invoice
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableCard>
          <Pagination page={page} pageSize={PAGE} total={rowsTotal} onPage={setPage} />
        </>
      )}

      {/* Invoice detail drill-down */}
      <Modal open={!!invoiceDetail} onClose={() => setInvoiceDetail(null)} size="lg"
        title={invoiceDetail ? `${invoiceDetail.supplier_name} · ${dt(invoiceDetail.date)}` : ""}>
        {invoiceDetail && (
          <div className="space-y-4">
            <TableCard className="!shadow-none">
              <thead className={theadCls}>
                <tr><th className={thCls}>Product</th><th className={thNumCls}>Qty</th><th className={thNumCls}>Cost</th><th className={thNumCls}>Total</th></tr>
              </thead>
              <tbody>
                {invItems.length === 0 && <EmptyRow colSpan={4} icon={FileText}>Loading…</EmptyRow>}
                {invItems.map((p) => (
                  <tr key={p.id} className={rowCls}>
                    <td data-label="Product" className={tdCls}>{p.product_name}</td>
                    <td data-label="Qty" className={tdNumCls}>{p.quantity}</td>
                    <td data-label="Cost" className={tdNumCls}>{fmtMoney(p.purchase_price)}</td>
                    <td data-label="Total" className={`${tdNumCls} font-semibold`}>{fmtMoney(p.total)}</td>
                  </tr>
                ))}
              </tbody>
            </TableCard>
            <div className="flex items-center justify-between text-sm flex-wrap gap-3">
              <div className="space-x-4">
                <span className="text-agri-ink-2">Total <span className="font-semibold text-agri-ink tabular-nums">{fmtMoney(invoiceDetail.total)}</span></span>
                <span className="text-agri-ink-2">Paid <span className="font-semibold text-agri-ink tabular-nums">{fmtMoney(invoiceDetail.paid)}</span></span>
                <span className="text-agri-ink-2">Payable <span className={`font-semibold tabular-nums ${invoiceDetail.credit > 0 ? "text-agri-danger" : "text-agri-forest"}`}>{fmtMoney(invoiceDetail.credit)}</span></span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => { const g = invoiceDetail; setInvoiceDetail(null); openInvEdit(g); }} className={btnOutline + " flex items-center gap-2"}><Pencil size={15} /> Edit</button>
                <button onClick={() => downloadPurchaseGroupInvoice(invoiceDetail)} className={btnPrimary + " flex items-center gap-2"}><Download size={15} /> Download</button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit invoice — per-line quantities, remove lines, date/invoice # */}
      <Modal open={!!editInv} onClose={() => setEditInv(null)} size="lg"
        title={editInv ? `Edit Invoice · ${editInv.supplier_name} · ${dt(editInv.date)}` : ""}>
        {editInv && (
          <form onSubmit={(e) => { e.preventDefault(); saveInv.mutate(); }} className="space-y-4">
            <div className="space-y-2 max-h-[42vh] overflow-y-auto -mr-1 pr-1">
              {editLines.map((l) => (
                <div key={l.id} className={`flex items-center gap-3 border rounded-lg p-2.5 ${l.removed ? "opacity-50 border-red-200 bg-red-50/40" : "border-[rgba(28,28,30,0.06)]"}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{l.name}</div>
                    <div className="text-xs text-agri-ink-2">{fmtMoney(l.cost)} / unit · line {fmtMoney((parseFloat(l.qty) || 0) * l.cost)}</div>
                  </div>
                  <div className="w-24">
                    <NumberInput value={l.qty} disabled={l.removed} onChange={(n) => setEditQty(l.id, n)} />
                  </div>
                  <button type="button" onClick={() => toggleRemove(l.id)} title={l.removed ? "Keep this line" : "Remove this line"}
                    className={`p-1.5 rounded ${l.removed ? "text-agri-forest hover:bg-agri-forest/10" : "text-agri-ink-2 hover:text-agri-danger hover:bg-red-50"}`}>
                    {l.removed ? <Plus size={15} /> : <Trash2 size={15} />}
                  </button>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Invoice date</label><input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Invoice #</label><input value={editInvNo} onChange={(e) => setEditInvNo(e.target.value)} className={inputCls} /></div>
            </div>
            <div className="flex justify-between items-center text-sm border-t border-[rgba(28,28,30,0.06)] pt-3">
              <span className="text-agri-ink-2">New invoice total</span>
              <span className="tabular-nums font-bold text-agri-forest">{fmtMoney(editTotal)}</span>
            </div>
            <p className="text-[11px] text-agri-ink-2">Saving reverses the old lines and re-applies these — stock, the supplier's payable and cash all update. Cost per unit stays as booked (change it in Products → Edit).</p>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setEditInv(null)} className={btnOutline}>Cancel</button>
              <button className={btnPrimary} disabled={saveInv.isPending}>{saveInv.isPending ? "Saving…" : "Save changes"}</button>
            </div>
          </form>
        )}
      </Modal>

      <Modal open={open} onClose={() => setOpen(false)} title="Record Purchase" size="lg">
        <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Supplier</label>
              <select required value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })} className={inputCls}>
                <option value="">Select…</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select></div>
            <div><label className={labelCls}>Product</label>
              <select required value={form.product_id} onChange={(e) => {
                const prod = products.find((p) => p.id === e.target.value);
                setForm({
                  ...form, product_id: e.target.value,
                  supplier_id: form.supplier_id || prod?.supplier_id || "",
                  reorder_level: form.reorder_level === "" && prod?.reorder_level != null ? prod.reorder_level : form.reorder_level,
                });
              }} className={inputCls}>
                <option value="">Select…</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select></div>
            <div><label className={labelCls}>Quantity</label>
              <NumberInput required emptyValue={1} value={form.quantity}
                onChange={(n) => { setForm({ ...form, quantity: n }); fe.clearField("quantity"); }}
                className={fe.fieldError("quantity") ? inputErrCls : ""} />
              <FieldError>{fe.fieldError("quantity")}</FieldError></div>
            <div><label className={labelCls}>Cost/unit</label>
              <input value={form.product_id ? fmtMoney(unitCost) : "—"} disabled className={inputCls + " bg-agri-bg text-agri-ink-2"} />
              <p className="text-[11px] text-agri-ink-2 mt-1">From the product. Change it in Products → Edit.</p></div>
            <div><label className={labelCls}>Reorder level</label>
              <NumberInput value={form.reorder_level} emptyValue="" onChange={(n) => setForm({ ...form, reorder_level: n })} /></div>
            <div><label className={labelCls}>Expiry date</label>
              <input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Purchase date</label>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Invoice #</label><input value={form.invoice_no} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} className={inputCls} /></div>
          </div>

          <div>
            <label className={labelCls}>Payment</label>
            <div className="grid grid-cols-3 gap-2">
              {["paid", "partial", "credit"].map((m) => (
                <button key={m} type="button" onClick={() => setPayMethod(m)}
                  className={`text-xs font-medium px-3 py-2 rounded-lg border capitalize transition-colors ${payMethod === m ? "bg-agri-forest text-white border-agri-forest" : "bg-white border-[rgba(28,28,30,0.12)] text-agri-ink hover:bg-agri-bg"}`}>
                  {m === "credit" ? "On credit" : m}
                </button>
              ))}
            </div>
            {payMethod === "partial" && (
              <div className="mt-2">
                <label className={labelCls}>Amount paid now</label>
                <NumberInput value={form.paid_amount} max={billTotal} onChange={(n) => setForm({ ...form, paid_amount: n })} />
              </div>
            )}
            <div className="mt-2 flex justify-between text-sm">
              <span className="text-agri-ink-2">Total: <span className="tabular-nums font-medium text-agri-ink">{fmtMoney(billTotal)}</span></span>
              <span className="text-agri-ink-2">Owed to supplier: <span className={`tabular-nums font-semibold ${owed > 0 ? "text-agri-danger" : "text-agri-forest"}`}>{fmtMoney(owed)}</span></span>
            </div>
          </div>

          <div><label className={labelCls}>Notes</label><input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} /></div>
          <div className="flex justify-end gap-2 pt-2"><button type="button" onClick={() => setOpen(false)} className={btnOutline}>Cancel</button><button className={btnPrimary} disabled={save.isPending}>Record</button></div>
        </form>
      </Modal>

      <Modal open={!!payEdit} onClose={() => setPayEdit(null)} title="Edit Payment" size="md">
        {payEdit && (
          <form onSubmit={(e) => { e.preventDefault(); savePay.mutate(); }} className="space-y-3">
            <div className="text-sm text-agri-ink-2">
              {payEdit.supplier_name} · {payEdit.product_name} · Total <span className="font-semibold text-agri-ink">{fmtMoney(eTotal)}</span>
            </div>
            <div>
              <label className={labelCls}>Payment</label>
              <div className="grid grid-cols-3 gap-2">
                {["paid", "partial", "credit"].map((m) => (
                  <button key={m} type="button" onClick={() => setEm(m)}
                    className={`text-xs font-medium px-3 py-2 rounded-lg border capitalize transition-colors ${em === m ? "bg-agri-forest text-white border-agri-forest" : "bg-white border-[rgba(28,28,30,0.12)] text-agri-ink hover:bg-agri-bg"}`}>
                    {m === "credit" ? "On credit" : m}
                  </button>
                ))}
              </div>
              {em === "partial" && (
                <div className="mt-2">
                  <label className={labelCls}>Amount paid</label>
                  <NumberInput value={ePaid} max={eTotal} onChange={setEPaid} />
                </div>
              )}
              <div className="mt-2 flex justify-between text-sm">
                <span className="text-agri-ink-2">Paid: <span className="tabular-nums font-medium text-agri-ink">{fmtMoney(eNewPaid)}</span></span>
                <span className="text-agri-ink-2">Owed to supplier: <span className={`tabular-nums font-semibold ${eOwed > 0 ? "text-agri-danger" : "text-agri-forest"}`}>{fmtMoney(eOwed)}</span></span>
              </div>
            </div>
            <p className="text-[11px] text-agri-ink-2">Updates this purchase and the supplier's payable everywhere (Payables ledger, supplier profile).</p>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setPayEdit(null)} className={btnOutline}>Cancel</button>
              <button className={btnPrimary} disabled={savePay.isPending}>{savePay.isPending ? "Saving…" : "Save payment"}</button>
            </div>
          </form>
        )}
      </Modal>

      <Modal open={!!retPur} onClose={() => setRetPur(null)} title="Return to Supplier" size="md">
        {retPur && (
          <form onSubmit={(e) => { e.preventDefault(); doReturn.mutate(); }} className="space-y-3">
            <div className="text-sm text-agri-ink-2">
              {retPur.supplier_name} · {retPur.product_name} · bought <span className="font-semibold text-agri-ink">{retPur.quantity}</span> @ {fmtMoney(retPur.purchase_price)}
            </div>
            <div>
              <label className={labelCls}>Quantity to return</label>
              <NumberInput data-testid="return-qty-input" required value={retQty} max={retPur.quantity} onChange={(n) => setRetQty(n)} />
              <p className="text-[11px] text-agri-ink-2 mt-1">Up to {retPur.quantity} (and no more than what's in stock).</p>
            </div>
            <div>
              <label className={labelCls}>Settlement</label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setRetSettle("credit")}
                  className={`text-xs font-medium px-3 py-2 rounded-lg border transition-colors ${retSettle === "credit" ? "bg-agri-forest text-white border-agri-forest" : "bg-white border-[rgba(28,28,30,0.12)] text-agri-ink hover:bg-agri-bg"}`}>
                  Reduce payable
                </button>
                <button type="button" onClick={() => setRetSettle("cash")}
                  className={`text-xs font-medium px-3 py-2 rounded-lg border transition-colors ${retSettle === "cash" ? "bg-agri-forest text-white border-agri-forest" : "bg-white border-[rgba(28,28,30,0.12)] text-agri-ink hover:bg-agri-bg"}`}>
                  Cash refund
                </button>
              </div>
              <p className="text-[11px] text-agri-ink-2 mt-1">
                {retSettle === "credit"
                  ? "Reduces what you owe this supplier (payable). Use when the purchase was on credit."
                  : "Supplier hands cash back → Cash in Hand goes up. Use when you'd already paid."}
              </p>
            </div>
            <div><label className={labelCls}>Notes</label><input value={retNotes} onChange={(e) => setRetNotes(e.target.value)} className={inputCls} /></div>
            <div className="flex justify-between items-center text-sm pt-1">
              <span className="text-agri-ink-2">Return value</span>
              <span className="tabular-nums font-semibold text-agri-danger">{fmtMoney(retTotal)}</span>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setRetPur(null)} className={btnOutline}>Cancel</button>
              <button data-testid="return-save-btn" className={btnPrimary} disabled={doReturn.isPending}>{doReturn.isPending ? "Saving…" : "Record return"}</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
